import { createContext } from 'react';
import {
	IItemForGetShortInfo,
	TFirmwareType,
	IDishesForAssign,
	IDataGetCategoriesByIDRestaurantForAssignDishes,
	IDataGetSubcategoriesByIDsCategoriesForAssignDishes,
	IDishesIDsSubcategoriesForAssignDishes,
	IDataGetDishesByIDsSubcategoriesForAssignDishes,
} from 'types';

export type TSelectedItem = Nullable<string>;
export type TDishesSortBy = '' | 'NameA-Z' | 'NameZ-A' | 'Price1-10' | 'Price10-1';
export type TIDSelectedTypeFirmware = null | TFirmwareType;
export type TActiveItemToDisplay = 'categories' | 'subCategories' | 'dishes';
export type TNotificationsForCategory = 'showNothing' | 'showLoading' | 'showNotification';
export type TTypeOfModalWindow = null | 'toConfirmFieldsClearing' | 'toConfirmSendChanges' | 'success' | 'error';
export interface IIDsSelectedItems {
	[key: string]: boolean; // key - is the ID and boolean means open or closed tab status.
}
export interface IDishWithAdditionalField extends IDishesIDsSubcategoriesForAssignDishes {
	isAddedDishFromList: boolean;
}
export interface IDishesWithAdditionalProperty extends IDataGetDishesByIDsSubcategoriesForAssignDishes {
	isChanged: boolean;
	dishes: IDishWithAdditionalField[];
}
export type TCountOfItems = '12' | '24' | '48';

export interface IDishAssignmentState {
	dishesForAssign: IDishesForAssign;
	searchString: string;
	searchStringForCategories: string;
	searchStringForSubcategories: string;
	searchStringForDishes: string;
	IDSelectedCustomer: TSelectedItem;
	fetchedRestaurants: IItemForGetShortInfo[];
	IDSelectedRestaurant: TSelectedItem;
	price: string;
	isUnassignedDishes: boolean;
	sortBy: TDishesSortBy;
	IDsSelectedDishes: string[];
	categories: IDataGetCategoriesByIDRestaurantForAssignDishes[];
	IDsSelectedCategories: IIDsSelectedItems;
	subcategories: IDataGetSubcategoriesByIDsCategoriesForAssignDishes[];
	IDsSelectedSubcategories: IIDsSelectedItems;
	dishes: IDishesWithAdditionalProperty[];
	activeItemToDisplay: TActiveItemToDisplay;
	isShowBreadcrumbs: boolean;
	notificationsForCategory: TNotificationsForCategory;
	typeOfModalWindow: TTypeOfModalWindow;
	isShowModalWindowToCreateSubcategory: boolean;
	currentPage: number;
	countOfItems: TCountOfItems;
	// below is the data for the modal window with additional restaurants
	isVisibleAdditionalRestaurantsModal: boolean;
	isApplyToOne: boolean;
	selectedAdditionalRestaurants: string[];
	selectSearchString: string;
}

export const dishAssignmentInitialState: IDishAssignmentState = {
	dishesForAssign: { dishesCount: 0, dishes: [] },
	searchString: '',
	searchStringForCategories: '',
	searchStringForSubcategories: '',
	searchStringForDishes: '',
	IDSelectedCustomer: null,
	fetchedRestaurants: [],
	IDSelectedRestaurant: null,
	price: '',
	isUnassignedDishes: false,
	sortBy: '',
	IDsSelectedDishes: [],
	categories: [],
	IDsSelectedCategories: {},
	subcategories: [],
	IDsSelectedSubcategories: {},
	dishes: [],
	activeItemToDisplay: 'categories',
	isShowBreadcrumbs: false,
	notificationsForCategory: 'showNothing',
	typeOfModalWindow: null,
	isShowModalWindowToCreateSubcategory: false,
	currentPage: 1,
	countOfItems: '12',
	// below is the data for the modal window with additional restaurants
	isVisibleAdditionalRestaurantsModal: false,
	isApplyToOne: false,
	selectedAdditionalRestaurants: [],
	selectSearchString: '',
};

export interface IDishAssignmentContext extends IDishAssignmentState {
	changeSearchString(value: string): void;
	changeSearchStringForCategories(value: string): void;
	changeSearchStringForSubcategories(value: string): void;
	changeSearchStringForDishes(value: string): void;
	setIDSelectedCustomer(value: TSelectedItem): void;
	setFetchedRestaurants(value: IItemForGetShortInfo[]): void;
	setIDSelectedRestaurant(value: TSelectedItem): void;
	setPrice(value: string): void;
	resetSelectedControls(): void;
	changeIsUnassignedDishesStatus(value: boolean): void;
	setSortBy(sortBy: TDishesSortBy): void;
	changeCurrentPage(page: number): void;
	setIDsSelectedDishes(value: string[]): void;
	setIDsSelectedCategories(value: IIDsSelectedItems, isUpdateSubcategoriesList: boolean): Promise<void>;
	setIDsSelectedSubcategories(value: IIDsSelectedItems, isUpdateDishesList: boolean): Promise<void>;
	changeActiveItemToDisplay(value: TActiveItemToDisplay): void;
	setShowBreadcrumbs(value?: boolean): void;
	addSelectedDishesToSubcategory(): void;
	deleteDishFromSubcategory(subcategoryID: string, dishID: string): void;
	changeTypeOfModalWindow(value: TTypeOfModalWindow): void;
	changeIsShowModalWindowToCreateSubcategory(value: boolean): void;
	resetChanges(): void;
	sendChanges(): Promise<void>;
	updateModuleDataAfterSaving(): void;
	updateCollectionOfCategories(): void;
	// below is the data for the modal window with additional restaurants
	setVisibleAdditionalRestaurantsModal(value: boolean): void;
	toggleApplyToOne(): void;
	setAdditionalRestaurants(value: string[]): void;
	changeSelectSearchString(value: string): void;
}

export const DishAssignmentContext = createContext<IDishAssignmentContext>({
	changeSearchString: () => undefined,
	changeSearchStringForCategories: () => undefined,
	changeSearchStringForSubcategories: () => undefined,
	changeSearchStringForDishes: () => undefined,
	setIDSelectedCustomer: () => undefined,
	setFetchedRestaurants: () => undefined,
	setIDSelectedRestaurant: () => undefined,
	setPrice: () => undefined,
	resetSelectedControls: () => undefined,
	changeIsUnassignedDishesStatus: () => undefined,
	setSortBy: () => undefined,
	changeCurrentPage: () => undefined,
	setIDsSelectedDishes: () => undefined,
	setIDsSelectedCategories: async () => undefined,
	setIDsSelectedSubcategories: async () => undefined,
	changeActiveItemToDisplay: () => undefined,
	setShowBreadcrumbs: () => undefined,
	addSelectedDishesToSubcategory: () => undefined,
	deleteDishFromSubcategory: () => undefined,
	changeTypeOfModalWindow: () => undefined,
	changeIsShowModalWindowToCreateSubcategory: () => undefined,
	resetChanges: () => undefined,
	sendChanges: async () => undefined,
	updateModuleDataAfterSaving: () => undefined,
	updateCollectionOfCategories: () => undefined,
	// below is the data for the modal window with additional restaurants
	setVisibleAdditionalRestaurantsModal: () => undefined,
	toggleApplyToOne: () => undefined,
	setAdditionalRestaurants: () => undefined,
	changeSelectSearchString: () => undefined,
	...dishAssignmentInitialState,
});
